import getConfig from 'next/config';

import {getApiRequest, patchApiRequest} from '~/src/utilities/feed/api.js';
import {ProfileData} from '~/src/types/user';
import {login, userIsLoggedIn} from '~/src/utilities/helpers/account';
import {refreshOrShouldLogin} from '~/src/utilities/helpers/login';
import {Logger} from '~/src/utilities/helpers/log';

const {publicRuntimeConfig: config} = getConfig();

interface GetUserProfileConfig {
    forceLogin?: boolean;
}

interface UserReactions {
    received: {
        [key: string]: number;
    };
    awarded: {
        [key: string]: number;
    };
}

const getUserProfileDefaultConfig = {
    forceLogin: true
};

export const getUserProfile = async (configOverrides?: GetUserProfileConfig): Promise<ProfileData | void> => {
    const {forceLogin} = {
        ...getUserProfileDefaultConfig,
        ...configOverrides
    };

    if (forceLogin) {
        const isLoggedIn = userIsLoggedIn();
        if (!isLoggedIn || (await refreshOrShouldLogin(isLoggedIn))) {
            return login();
        }
    }

    try {
        return await getApiRequest(config.user.service.profile);
    } catch (error) {
        Logger.info(`[${error.name}][Profile] Error while retrieving user profile`, {error});
        throw error;
    }
};

export const updateUserProfile = async (profile: ProfileData): Promise<any | void> => {
    const isLoggedIn = userIsLoggedIn();
    if (!isLoggedIn || (await refreshOrShouldLogin(isLoggedIn))) {
        return login();
    }

    try {
        await patchApiRequest(`${config.user.service.profile}?tenantId=${config.tenantId}`, {...profile, email: undefined});
    } catch (error) {
        if (error.statusCode === 422) {
            return await error.raw.json();
        }

        Logger.info(`[${error.name}][Profile] Error while updating user profile`, {error});
        throw error;
    }
};

export const getUserReactions = async (): Promise<UserReactions> => {
    try {
        return await getApiRequest(config.comment.service.userReactions);
    } catch (error) {
        if (error.statusCode === 422) {
            return await error.raw.json();
        }

        Logger.info(`[${error.name}][Profile] Error while fetching user reactions`, {error});
        throw error;
    }
};
